import "devextreme/dist/css/dx.light.css";
import "./styles/styles.scss";
import { ThemeProvider, createTheme } from "@mui/material";
import { Route, Routes } from "react-router-dom";
import { ParallaxProvider } from "react-scroll-parallax";
import Layout from "./components/layouts/main";
import AppRoutes from "./_routes/app-routes";
import ScrollToTop from "./components/scroll-to-top/scroll-to-top";
import { Helmet, HelmetProvider } from "react-helmet-async";
import useMeta from "./hooks/useMeta";
import CookieConsentContainer from "./components/cookie-consent/cookie-consent-container";
import { GAContextProvider } from "./_contexts/ga-context/ga-context";
import config from "devextreme/core/config";
import EnvironmentLogin from "./components/env-login/env-login";
import { useEffect, useState } from "react";
import ScriptLoader from "./components/script-loader";
import ZendeskCustomLauncherHandler from "./components/zendesk-chatbot/zendesk-custom-launcher-handler";
import fetchDynamicRoutes from "./_routes/dynamic-routes";
import { NotificationsProvider } from "./_contexts/notifications-context";
import { library } from "@fortawesome/fontawesome-svg-core";
import { fas } from "@fortawesome/free-solid-svg-icons";
import SplashScreen from "./components/splash-screen";
import Error404 from "./pages/404";
import AuthError from "./pages/auth-error";
import Loader from "./components/loader";
import { showAppAuthentication } from "./utils/utils";

library.add(fas);

config({
    licenseKey: process.env.REACT_APP_DEVEXTREME_LICENSE_KEY
});

const theme = createTheme({
    palette: {
        primary: {
            main: "#003366"
        },
        secondary: {
            main: "#cd9700"
        }
    }
});

const zendesk = {
    script: `https://static.zdassets.com/ekr/snippet.js?key=${process.env.REACT_APP_ZENDESK_KEY}`,
    id: "ze-snippet"
};

function App() {
    const meta = useMeta();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [chatbotLoaded, setChatbotLoaded] = useState(false);
    const [dynamicRoutes, setDynamicRoutes] = useState([]);
    const [loadingRoutes, setLoadingRoutes] = useState(true);
    const [appRoutes, setAppRoutes] = useState([]);
    const [authChecked, setAuthChecked] = useState(!showAppAuthentication); // New state to track if auth check is done


    useEffect(() => {
        if (!showAppAuthentication) return;
        // Check if the user is already authenticated
        const auth = sessionStorage.getItem("authenticated");
        if (auth === "true" || !showAppAuthentication) {
            setIsAuthenticated(true);
        }
        setAuthChecked(true);
    }, []);

    useEffect(() => {
        const initializeRoutes = async () => {
            const divisionCodes = ["RVR", "AUD", "TXC"];
            const allRoutes = [];

            for (const divisionCode of divisionCodes) {
                const fetchedRoutes = await fetchDynamicRoutes(divisionCode);
                if (fetchedRoutes) {
                    allRoutes.push(...fetchedRoutes);
                    allRoutes.filter(route => route.path !== "*"); // Exclude wildcard routes
                }
            }
            setAppRoutes(AppRoutes);
            setDynamicRoutes(allRoutes);
            setLoadingRoutes(false);
        };

        initializeRoutes();
    }, []);

    const handleScriptLoad = () => {
        setChatbotLoaded(true);
    };

    const handleLogin = () => {
        setIsAuthenticated(true);
    };
    const pageTitle = meta?.title || "Loading...";
    const pageDesc = meta?.description || "Preparing content";

    if (showAppAuthentication && !authChecked) {
        return <Loader />;
    }

    // Render login page only for local, development or QA environments
    if (showAppAuthentication && !isAuthenticated) {
        return <EnvironmentLogin onLogin={handleLogin} />;
    }

    return (
        <>
            <ScriptLoader scriptSrc={zendesk.script} scriptId={zendesk.id} onLoad={handleScriptLoad} />
            {chatbotLoaded && <ZendeskCustomLauncherHandler />}
            <GAContextProvider>
                <NotificationsProvider>
                    <CookieConsentContainer />
                    <HelmetProvider>
                        <ThemeProvider theme={theme}>
                            <div className="App">
                                <Helmet>
                                    <title>{pageTitle}</title>
                                    <meta name="description" content={pageDesc} />
                                </Helmet>
                                <ParallaxProvider>
                                    <Layout>
                                        <SplashScreen />
                                        {loadingRoutes ? (
                                            <Loader />
                                        ) : (
                                            <Routes>
                                                {appRoutes.map(route => (
                                                    <Route
                                                        path={route.path}
                                                        element={<ScrollToTop>{route.component}</ScrollToTop>}
                                                        key={route.path}
                                                    />
                                                ))}
                                                {dynamicRoutes.map(route => {
                                                    return (
                                                        <Route
                                                            path={route.path}
                                                            element={<ScrollToTop>{route.component}</ScrollToTop>}
                                                            key={route.path}
                                                        />
                                                    )
                                                })}
                                                <Route path="*" element={<ScrollToTop><Error404 /></ScrollToTop>} />
                                                <Route path="/autherror" element={<ScrollToTop><AuthError /></ScrollToTop>} />
                                            </Routes>
                                        )}
                                    </Layout>
                                </ParallaxProvider>
                            </div>
                        </ThemeProvider>
                    </HelmetProvider>
                </NotificationsProvider>
            </GAContextProvider>
        </>
    );
}

export default App;
